<template>
  <v-container fluid class="policy-page pa-0">
    <div class="policy-backdrop" aria-hidden="true" />

    <v-card class="policy-card" elevation="0" rounded="0">
      <div class="policy-inner">

        <div class="policy-hero">
          <img :src="logo" alt="Wall Labs" class="policy-logo" />
          <div class="hero-text">
            <h1 class="policy-title">
              <v-icon icon="mdi-shield-lock-outline" class="title-icon" />
              Privacy Policy
            </h1>
            <p class="policy-meta">
              <v-icon icon="mdi-calendar-check" size="18" class="meta-icon" />
              <span class="brand-wall">Wall</span>
              <span class="brand-labs">Labs</span>
              · Effective May 20, 2026 · Last Updated May 20, 2026
            </p>
          </div>
        </div>

        <v-divider class="policy-divider" />

        <div class="policy-body">
          <v-alert
            type="info"
            variant="tonal"
            density="comfortable"
            rounded="lg"
            class="intro-alert text-left"
            icon="mdi-information-outline"
          >
          Wall Labs respects your privacy. By using Wall Labs, you agree to this
          Privacy Policy.
        </v-alert>

        <section
          v-for="section in sections"
          :key="section.id"
          class="policy-section"
        >
          <div class="section-head">
            <v-avatar color="primary" variant="tonal" size="40" rounded="lg">
              <v-icon :icon="section.icon" size="22" />
            </v-avatar>
            <h2>{{ section.title }}</h2>
          </div>

          <div class="section-content">
            <p v-if="section.lead" class="section-lead">{{ section.lead }}</p>

            <ul v-if="section.items?.length" class="section-list">
            <li v-for="(item, idx) in section.items" :key="idx">
              <v-icon
                v-if="item.icon"
                :icon="item.icon"
                size="18"
                class="item-icon"
              />
              <span v-html="item.text" />
              <ul v-if="item.sub?.length" class="sub-list">
                <li v-for="(sub, sidx) in item.sub" :key="sidx">
                  <v-icon icon="mdi-circle-small" size="20" class="sub-icon" />
                  {{ sub }}
                </li>
              </ul>
            </li>
            </ul>

            <p v-if="section.footer" class="section-footer">{{ section.footer }}</p>
          </div>
        </section>
        </div>

        <v-divider class="policy-divider" />

  
      </div>
    </v-card>
  </v-container>
</template>

<script>
import logo from '@/assets/wall-labs-logo.png'

export default {
  name: 'PrivacyPolicyPage',
  data() {
    return {
      logo,
      sections: [
        {
          id: 1,
          icon: 'mdi-database-outline',
          title: '1) What Wall Labs Collects',
          lead:
            'Wall Labs does not require account creation and does not collect direct personal identifiers like your name, phone number, or email through in-app registration. The app does process the following categories of data as part of normal operation:',
          items: [
            {
              icon: 'mdi-cellphone-cog',
              text: '<strong>Locally stored app preferences on your device</strong>',
              sub: [
                'Favorite wallpaper URLs',
                'Theme color selection',
                'Reward-action state for ad-based unlocks (remaining unlocked actions and first free action usage)'
              ]
            },
            {
              icon: 'mdi-cloud-sync-outline',
              text: '<strong>Network request data</strong>',
              sub: [
                'Requests to the Wall Labs wallpaper catalog API to fetch wallpaper listings',
                'Image download requests when you preview, apply, or save wallpapers'
              ]
            },
            {
              icon: 'mdi-advertisements',
              text: '<strong>Advertising-related data (Google Mobile Ads)</strong>',
              sub: [
                'Rewarded ads are used for certain actions',
                'Google may process device/app identifiers and related ad-serving data under Google’s own policies'
              ]
            }
          ]
        },
        {
          id: 2,
          icon: 'mdi-cog-outline',
          title: '2) How Data Is Used',
          lead: 'Wall Labs uses data only to provide app functionality, including:',
          items: [
            { icon: 'mdi-image-multiple', text: 'Loading and displaying wallpaper content' },
            { icon: 'mdi-heart-outline', text: 'Letting you save favorites' },
            { icon: 'mdi-palette-outline', text: 'Remembering selected theme preferences' },
            { icon: 'mdi-gift-outline', text: 'Managing rewarded-ad unlock flow' },
            { icon: 'mdi-download', text: 'Saving wallpapers to your gallery (when you choose to save)' },
            {
              icon: 'mdi-cellphone-arrow-down',
              text: 'Applying wallpapers on supported Android devices (when you choose to apply)'
            }
          ]
        },
        {
          id: 3,
          icon: 'mdi-folder-lock-outline',
          title: '3) Permissions',
          lead: 'Depending on your device and action, Wall Labs may request:',
          items: [
            {
              icon: 'mdi-image-outline',
              text: '<strong>Photos/Storage permission</strong> to save wallpapers to your gallery. Wall Labs requests this permission only for that feature and does not require it for basic browsing.'
            }
          ]
        },
        {
          id: 4,
          icon: 'mdi-handshake-outline',
          title: '4) Third-Party Services',
          lead: 'Wall Labs uses third-party services:',
          items: [
            { icon: 'mdi-advertisements', text: 'Google Mobile Ads (AdMob) for rewarded ads' },
            {
              icon: 'mdi-cloud-outline',
              text: 'External content delivery endpoints for wallpaper metadata and image files'
            }
          ],
          footer:
            'These providers may process technical data (such as IP address, device details, and request metadata) according to their own privacy policies.'
        },
        {
          id: 5,
          icon: 'mdi-share-variant-outline',
          title: '5) Data Sharing',
          lead: 'Wall Labs does not sell your personal data.',
          footer:
            'Data may be shared only as necessary to operate the app through service providers (such as ad serving and content delivery), or when legally required.'
        },
        {
          id: 6,
          icon: 'mdi-clock-outline',
          title: '6) Data Retention',
          footer:
            'Data stored locally in the app (favorites, theme preference, ad unlock state) remains on your device until you clear app data or uninstall the app. Server- and provider-side retention (for ad/content services) is governed by those providers’ own retention policies.'
        },
        {
          id: 7,
          icon: 'mdi-account-child-outline',
          title: '7) Children’s Privacy',
          footer:
            'Wall Labs is not directed to children under 13. If you believe a child has provided personal data through Wall Labs, stop using the service and contact the relevant platform/provider support channels.'
        },
        {
          id: 8,
          icon: 'mdi-shield-check-outline',
          title: '8) Security',
          footer:
            'Wall Labs uses standard platform and network mechanisms to operate the service. No method of transmission or storage can be guaranteed 100% secure.'
        },
        {
          id: 9,
          icon: 'mdi-toggle-switch-outline',
          title: '9) Your Choices',
          lead: 'You can:',
          items: [
            {
              icon: 'mdi-delete-outline',
              text: 'Remove favorites/theme data by clearing app data or uninstalling the app'
            },
            {
              icon: 'mdi-lock-outline',
              text: 'Deny or revoke photo/storage permission through device settings'
            },
            {
              icon: 'mdi-tune',
              text: 'Control ad preferences through your device and Google ad settings where available'
            }
          ]
        },
        {
          id: 10,
          icon: 'mdi-file-document-edit-outline',
          title: '10) Changes to This Policy',
          footer:
            'This Privacy Policy may be updated from time to time. Updated versions will be posted on the Wall Labs website with the revised date.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.policy-page {
  position: relative;
  min-height: 100vh;
}

.policy-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(ellipse 120% 80% at 50% -20%, rgba(124, 77, 255, 0.28), transparent 50%),
    radial-gradient(ellipse 90% 60% at 100% 50%, rgba(6, 182, 212, 0.18), transparent 45%),
    linear-gradient(165deg, #0b1229 0%, #121e3f 45%, #0d1530 100%);
}

.policy-card {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
  background: #ffffff !important;
  border: none;
}

.policy-inner {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 20px 32px 40px;
  text-align: left;
}

.policy-toolbar {
  padding: 0 0 8px;
}

.policy-toolbar :deep(.v-btn) {
  justify-content: flex-start;
  padding-left: 0;
}

.policy-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 0 24px;
  text-align: left;
}

.policy-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  flex-shrink: 0;
}

.policy-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: #1a1f36;
}

.title-icon {
  color: #7c3aed;
}

.brand-wall {
  background: linear-gradient(135deg, #c026d3 0%, #7c3aed 45%, #06b6d4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  font-weight: 600;
}

.brand-labs {
  color: #334155;
  font-weight: 600;
}

.policy-meta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 4px;
  margin: 8px 0 0;
  font-size: 0.875rem;
  color: #64748b;
}

.meta-icon {
  color: #7c3aed;
  margin-right: 2px;
}

.policy-divider {
  margin: 0;
}

.policy-body {
  width: 100%;
  padding: 28px 0 8px;
  text-align: left;
}

.intro-alert {
  margin-bottom: 28px;
  text-align: left;
}

.intro-alert :deep(.v-alert__content) {
  text-align: left;
}

.policy-section {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e8ecf4;
}

.policy-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.policy-section {
  text-align: left;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  margin-bottom: 12px;
}

.section-content {
  padding-left: 0;
  margin-left: 0;
  max-width: 100%;
}

.section-head h2 {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #1a1f36;
}

.section-lead,
.section-footer {
  margin: 0 0 12px;
  color: #334155;
  line-height: 1.65;
  font-size: 0.9375rem;
  text-align: left;
}

.section-footer {
  margin-top: 8px;
}

.section-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.section-list > li {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  color: #2d3748;
  line-height: 1.6;
  font-size: 0.9375rem;
}

.item-icon {
  color: #5b21b6;
  margin-top: 2px;
  flex-shrink: 0;
}

.sub-list {
  list-style: none;
  width: 100%;
  margin: 6px 0 0 0;
  padding: 0 0 0 26px;
}

.sub-list li {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 4px;
  color: #4a5568;
  font-size: 0.875rem;
}

.sub-icon {
  color: #7c3aed;
  margin-left: -6px;
}

.policy-actions {
  padding: 24px 0 8px;
  text-align: left;
}

.action-btn {
  max-width: 280px;
}

@media (max-width: 600px) {
  .policy-inner {
    padding: 16px 20px 32px;
  }

  .policy-hero {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
